import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {
    LinkBox,
    LinkOverlay,
    Link,
    Text,
    useBreakpointValue,
    useStyleConfig
} from '@chakra-ui/react'
import PDCTitle from '../../../components/pdc-title'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import PDCImage from '../../../components/pdc-image/index.jsx'
import {useUrlFromLink} from '../../helpers/urlHelper.js'

/**
 * PDCAboutStoryLinksBlockTile is a presentational React component that renders a clickable tile for use in the PDC About Story Links block.
 *
 * This component displays a responsive image, a title, and a link label, all styled according to the provided props and design system. It supports dynamic image selection based on screen size, custom link handling (internal or external), and configurable layout based on the number of slides. The component is intended for use within a grid or carousel of story links, adapting its image aspect ratio and width responsively.
 *
 * Props:
 * @param {string} title - The title text displayed on the tile.
 * @param {string} linkLabel - The label for the clickable link.
 * @param {string} linkUrl - The fallback URL for the link if a custom link is not provided.
 * @param {string} color - The color for the title text.
 * @param {string} smallImage - The URL for the small breakpoint image.
 * @param {string} mediumImage - The URL for the medium breakpoint image.
 * @param {string} largeImage - The URL for the large breakpoint image.
 * @param {string} xlargeImage - The URL for the extra-large breakpoint image.
 * @param {string} xxlargeImage - The URL for the double extra-large breakpoint image.
 * @param {string} imageTitle - The title attribute for the image.
 * @param {string} imageAlt - The alt text for the image.
 * @param {number} displayPriority - The priority for image loading/display.
 * @param {number} nbSlides - The number of slides in the carousel/grid, used to determine image width.
 * @param {string} ratio - The aspect ratio for the image skeleton placeholder.
 * @param {object} custom - Optional custom configuration, including custom images and link URL/type.
 *
 * The component uses Chakra UI's LinkBox and LinkOverlay for accessibility and styling, and integrates with PDC-specific hooks and components for responsive image handling and routing.
 */
export const PDCAboutStoryLinksBlockTile = ({
    title,
    linkLabel,
    linkUrl,
    color,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    displayPriority,
    nbSlides,
    ratio,
    custom
}) => {
    const updatedUrl = useUrlFromLink(custom?.linkUrl) || linkUrl

    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    const styles = useStyleConfig('Link', {})
    styles.textDecoration = 'none'
    styles._hover = {
        textDecoration: 'none'
    }

    let imageWidths
    switch (nbSlides) {
        case 1:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.full
            break
        case 2:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
        case 3:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice
            break
        case 4:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.quarterandfullonsmalldevice
            break
        default:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
    }

    return (
        <LinkBox
            as="article"
            textAlign="center"
            textDecoration="none"
            height={'100%'}
            px={5}
            pb={[5, null, 0]}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'flex-start'}
        >
            <PDCImage
                currentImage={currentImage}
                imageWidths={imageWidths}
                imageAlt={imageAlt}
                imageTitle={imageTitle}
                imageProps={{height: '100%'}}
                displayPriority={displayPriority}
                title={title}
                width={'100%'}
                skeletonProps={{aspectRatio: ratio || '13 / 17', width: '100%'}}
            />
            <PDCTitle
                title={title}
                textColor={color}
                size={'2xl'}
                titleHeadingLevel={'h3'}
                mt={10}
            />

            <LinkOverlay
                as={custom?.linkUrl?.type === 'EXTERNAL' ? Link : RouteLink}
                target={custom?.linkUrl?.type === 'EXTERNAL' ? '_blank' : undefined}
                href={updatedUrl}
                to={updatedUrl}
                mt={6}
            >
                <Text variant="bodyBase1">{linkLabel}</Text>
            </LinkOverlay>
        </LinkBox>
    )
}

PDCAboutStoryLinksBlockTile.propTypes = {
    title: PropTypes.string.isRequired,
    linkLabel: PropTypes.string.isRequired,
    linkUrl: PropTypes.string.isRequired,
    color: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    displayPriority: PropTypes.bool,
    nbSlides: PropTypes.number,
    ratio: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    custom: PropTypes.object
}
